import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import clsx from 'clsx'

import { Box, Button, Card, TextField } from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import { DateRangePicker } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import BlackTooltip from 'components/common/tooltips'
import { useStyles } from 'components/core/Acquiring/AcquiringSettlements/components/styles'
import { Spinner } from 'components/core/Progress/Progress-component'
import { useDateRangePicker } from 'hooks/useDateRangePicker'
import {
    finishSettlementsCsvDownload,
    finishSettlementsPdfDownload,
    finishSettlementsPeriodSummaryDownload,
    startGetCardAcquiringSettlements,
    startSettlementsCsvDownload,
    startSettlementsPdfDownload,
    startSettlementsPeriodSummaryDownload,
} from 'redux/acquiring/acquiring-actions'
import { useAppSelector } from 'redux/hooks'
import { IMAGES_PATH, themeStyles } from 'themes'
import combineStyles from 'utils/combineStyles'

const pdfOval = IMAGES_PATH('/downloadOval.svg')
const csvOval = IMAGES_PATH('/upload-documents.svg')

interface IProps {
    classes: Record<string, string>
}

const AcquiringSettlementsFilter: FC<IProps> = props => {
    const style = useStyles()
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const [values, setValue] = useState<{ from?: Date | null | string; to?: Date | null | string }>({ from: null, to: null })
    const { from, to } = values
    const { dateRangeValue, handleDateRangeValue, dateRangeStart, dateRangeEnd } = useDateRangePicker('YYYY-MM-DD')

    const { loader } = useAppSelector(state => state.acquiring)

    const handleFilter = () => {
        dispatch(startGetCardAcquiringSettlements({ from, to }))
    }

    const handleDownload = (file: string) => {
        if (file === 'pdf') {
            dispatch(startSettlementsPdfDownload({ from, to }))
        }
        if (file === 'csv') {
            dispatch(startSettlementsCsvDownload({ from, to }))
        }
        if (file === 'period') {
            dispatch(startSettlementsPeriodSummaryDownload({ from, to }))
        }
    }

    useEffect(() => {
        setValue(prevState => ({ ...prevState, from: dateRangeStart || '', to: dateRangeEnd || '' }))
    }, [dateRangeStart, dateRangeEnd])

    useEffect(
        () => () => {
            dispatch(finishSettlementsPdfDownload())
            dispatch(finishSettlementsCsvDownload())
            dispatch(finishSettlementsPeriodSummaryDownload())
        },
        []
    )

    return (
        <Card classes={{ root: props.classes.card }}>
            <Box display="flex" alignItems="center" className={clsx(props.classes.box, style.box)}>
                <div className="dateContainer">
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        localeText={{ start: t('TransactionsFilter.From'), end: t('TransactionsFilter.To') }}
                    >
                        <DateRangePicker
                            value={dateRangeValue}
                            showToolbar={false}
                            onChange={handleDateRangeValue}
                            renderInput={(startProps, endProps) => {
                                startProps.inputProps = {
                                    ...startProps.inputProps,
                                    'data-sta-id': 'PaymentGateway-settlements-dateRangePicker-startDate',
                                }
                                endProps.inputProps = {
                                    ...endProps.inputProps,
                                    'data-sta-id': 'PaymentGateway-settlements-dateRangePicker-endDate',
                                }

                                return (
                                    <>
                                        <TextField className={style.dateRangeField} {...startProps} inputProps={startProps.inputProps} />
                                        <TextField className={style.dateRangeField} {...endProps} inputProps={endProps.inputProps} />
                                    </>
                                )
                            }}
                        />
                    </LocalizationProvider>
                </div>

                <div className={style.downloadBtnWrap}>
                    <br />
                    <div>
                        {loader?.SETTLEMENTS_PDF_DOWNLOAD ? (
                            <Spinner />
                        ) : (
                            <BlackTooltip data-clipboardtarget title={`${t('Acquiring.Download')} PDF`} placement="top" arrow={true}>
                                <img
                                    src={pdfOval}
                                    alt={`${t('Acquiring.Download')} PDF`}
                                    onClick={() => handleDownload('pdf')}
                                    className={style.downloadBtn}
                                />
                            </BlackTooltip>
                        )}
                    </div>

                    <div>
                        {loader?.SETTLEMENTS_CSV_DOWNLOAD ? (
                            <Spinner />
                        ) : (
                            <BlackTooltip data-clipboardtarget title={`${t('Acquiring.Download')} CSV`} placement="top" arrow={true}>
                                <img
                                    src={csvOval}
                                    alt={`${t('Acquiring.Download')} CSV`}
                                    onClick={() => handleDownload('csv')}
                                    className={style.downloadBtn}
                                />
                            </BlackTooltip>
                        )}
                    </div>
                    <div>
                        {loader?.SETTLEMENTS_PERIOD_SUMMARY_DOWNLOAD ? (
                            <Spinner />
                        ) : (
                            <BlackTooltip
                                data-clipboardtarget
                                title={`${t('Acquiring.Download')} Period summary`}
                                placement="top"
                                arrow={true}
                            >
                                <img
                                    src={csvOval}
                                    alt={`${t('Acquiring.Download')} Period summary`}
                                    onClick={() => handleDownload('period')}
                                    className={style.downloadBtn}
                                />
                            </BlackTooltip>
                        )}
                    </div>
                </div>

                <Button
                    onClick={handleFilter}
                    className={clsx(props.classes.filterButton, style.filterButton)}
                    variant="contained"
                    color="primary"
                >
                    {t('Acquiring.Filter')}
                </Button>
            </Box>
        </Card>
    )
}

export default withStyles(combineStyles(themeStyles.useAcquiringStyles, themeStyles.useTableStyles))(AcquiringSettlementsFilter)
